<script setup lang="ts">
import {
  useCartQuery,
  useDismissUnavailableCartLineMutation,
  useRemoveCartLineMutation,
  useRestoreUnavailableCartLineMutation,
  useUpdateCartLineMutation,
} from '~~/app/composables/queries/useStoreApi'
import { buildCartLineUpdatePayload } from '~~/app/composables/cart/updateCartLinePayload'
import { lineAtStockLimit } from '~~/app/composables/cart/stockLimit'
import { useCartStore } from '~~/stores/cartStore'
import { useAnalytics } from '~~/app/composables/useAnalytics'

const cartStore = useCartStore()
const { data: cart } = useCartQuery()
const updateCartLine = useUpdateCartLineMutation()
const removeCartLine = useRemoveCartLineMutation()
const analytics = useAnalytics()

const dismissUnavailable = useDismissUnavailableCartLineMutation()
const restoreUnavailable = useRestoreUnavailableCartLineMutation()

const lines = computed(() => cart.value?.lines ?? [])
const unavailableLines = computed(() => cart.value?.unavailableLines ?? [])
const isOpen = computed(() => cartStore.isDrawerOpen)
const route = useRoute()
const updatingLineId = ref<string | null>(null)
const stockLimitLineId = ref<string | null>(null)
const unavailableBusyLineId = ref<string | null>(null)

watch(() => route.fullPath, () => {
  if (cartStore.isDrawerOpen) {
    cartStore.closeDrawer()
  }
})

// Drawer jest zawsze zamontowany (layout) — wyzeruj zwrotkę przy zamknięciu,
// inaczej komunikat „brak stanu" pojawiłby się od razu po ponownym otwarciu.
watch(isOpen, (open) => {
  if (!open) {
    stockLimitLineId.value = null
  }
})

function close() {
  cartStore.closeDrawer()
}

function updateLine(lineId: string, currentQuantity: number, targetQuantity: number) {
  if (updatingLineId.value) return
  const isIncreasing = targetQuantity > currentQuantity
  const line = cart.value?.lines.find((l) => l.id === lineId)

  // Zwrotka na akcję: przy próbie zwiększenia ponad dostępny stan pokaż komunikat
  // od razu, bez wysyłania skazanego na błąd żądania do backendu.
  if (isIncreasing && line && !line.allowOutOfStock && lineAtStockLimit(line.stock, currentQuantity)) {
    stockLimitLineId.value = lineId
    return
  }

  updatingLineId.value = lineId
  if (!isIncreasing && stockLimitLineId.value === lineId) {
    stockLimitLineId.value = null
  }
  updateCartLine.mutate(buildCartLineUpdatePayload(lineId, currentQuantity, targetQuantity), {
    onSuccess: (result) => {
      const hasErrors = Array.isArray((result as { errors?: unknown })?.errors)
        && ((result as { errors: unknown[] }).errors).length > 0
      if (isIncreasing && hasErrors) {
        stockLimitLineId.value = lineId
      }
      else if (!hasErrors) {
        stockLimitLineId.value = null
        if (line) {
          analytics.trackCartQuantityDelta(line, targetQuantity - currentQuantity)
        }
      }
    },
    onSettled: () => {
      updatingLineId.value = null
    },
  })
}

function removeLine(lineId: string) {
  const line = cart.value?.lines.find((l) => l.id === lineId)
  removeCartLine.mutate({ lineId }, {
    onSuccess: () => {
      if (line) analytics.trackRemoveFromCart(line)
    },
  })
}

function dismissUnavailableLine(lineId: string) {
  if (unavailableBusyLineId.value) return
  unavailableBusyLineId.value = lineId
  dismissUnavailable.mutate({ lineId }, {
    onSettled: () => {
      unavailableBusyLineId.value = null
    },
  })
}

function restoreUnavailableLine(lineId: string) {
  if (unavailableBusyLineId.value) return
  unavailableBusyLineId.value = lineId
  restoreUnavailable.mutate({ lineId }, {
    onSettled: () => {
      unavailableBusyLineId.value = null
    },
  })
}
</script>

<template>
  <Transition name="cart-drawer">
    <div v-if="isOpen" class="cart-drawer" role="dialog" aria-modal="true" aria-label="Koszyk">
      <button class="cart-drawer__backdrop" type="button" aria-label="Zamknij koszyk klikając tło" @click="close" />

      <aside class="cart-drawer__panel">
        <header class="cart-drawer__header">
          <h2 class="cart-drawer__title">Koszyk</h2>
          <button type="button" class="cart-drawer__close" aria-label="Zamknij koszyk" @click="close">×</button>
        </header>

        <div class="cart-drawer__content">
          <p v-if="!lines.length && !unavailableLines.length" class="cart-drawer__empty">Koszyk jest pusty.</p>

          <div v-if="lines.length" class="cart-drawer__lines">
            <CartLineItem
              v-for="line in lines"
              :key="line.id"
              :line="line"
              :updating="updatingLineId === line.id"
              :stock-limit-reached="stockLimitLineId === line.id"
              @update-quantity="quantity => updateLine(line.id, line.quantity, quantity)"
              @remove="removeLine(line.id)"
            />
          </div>

          <!-- Wyprzedane na końcu koszyka, nie usuwane automatycznie: klient
               widzi, co się stało z produktem, i zamawia resztę normalnie. -->
          <section v-if="unavailableLines.length" class="cart-drawer__unavailable">
            <h3 class="cart-drawer__unavailable-title">
              Wyprzedane
              <span class="cart-drawer__unavailable-count">{{ unavailableLines.length }}</span>
            </h3>
            <div class="cart-drawer__lines">
              <CartLineItem
                v-for="line in unavailableLines"
                :key="line.id"
                :line="line"
                unavailable
                :restoring="unavailableBusyLineId === line.id"
                @remove="dismissUnavailableLine(line.id)"
                @restore="restoreUnavailableLine(line.id)"
              />
            </div>
          </section>

          <CartSummary :hide-cta-on-mobile="true" />
        </div>

        <div v-if="lines.length" class="cart-drawer__footer">
          <CartCheckoutFloat :total="cart?.total ?? 0" />
        </div>
      </aside>
    </div>
  </Transition>
</template>

<style scoped>
.cart-drawer { position: fixed; inset: 0; z-index: var(--z-overlay); display: flex; justify-content: flex-end; }
.cart-drawer__backdrop { position: absolute; inset: 0; border: 0; background: rgba(0,0,0,.35); cursor: pointer; }
.cart-drawer__panel { position: relative; width: min(100%, 460px); height: 100%; background: var(--color-white); display: flex; flex-direction: column; }
.cart-drawer__header { display: flex; align-items: center; justify-content: space-between; padding: 20px; border-bottom: 1px solid var(--color-border); flex-shrink: 0; }
.cart-drawer__content { padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 20px; flex: 1; min-height: 0; }
.cart-drawer__title { margin: 0; }
.cart-drawer__close {
  border: 0;
  background: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-black);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin: -10px -10px -10px 0;
  padding: 10px;
}
.cart-drawer__empty { margin: 0; }
.cart-drawer__unavailable {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}
.cart-drawer__unavailable-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  text-transform: uppercase;
}
.cart-drawer__unavailable-count {
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
}
.cart-drawer__footer { display: none; }
.cart-drawer-enter-active, .cart-drawer-leave-active { transition: opacity .2s ease; }
.cart-drawer-enter-from, .cart-drawer-leave-to { opacity: 0; }

@media (max-width: 960px) {
  .cart-drawer__footer {
    display: block;
    flex-shrink: 0;
  }
}

@media (max-width: 640px) {
  .cart-drawer__panel {
    width: calc(100% - 48px);
  }
}
</style>
